import { algoliasearch } from 'algoliasearch';

// Algolia configuration. Only search credentials should be exposed to the browser.
const ALGOLIA_APP_ID = process.env.VUE_APP_ALGOLIA_APP_ID || '30MLPP6CTI';
const ALGOLIA_SEARCH_API_KEY = process.env.VUE_APP_ALGOLIA_SEARCH_API_KEY || 'eb03e5785b9ad1a5c198413a18401485';
const ALGOLIA_INDEX_NAME = process.env.VUE_APP_ALGOLIA_INDEX_NAME || 'videos';
const ALGOLIA_USERS_INDEX_NAME = process.env.VUE_APP_ALGOLIA_USERS_INDEX_NAME || 'users';

// Initialize Algolia client
const searchClient = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_API_KEY);

function createAlgoliaWriteNotConfiguredError() {
    const error = new Error('Algolia writes are handled by the backend stream Lambda');
    error.code = 'algolia_write_not_configured';
    return error;
}

/**
 * Search videos using Algolia
 * @param {string} query - Search query
 * @param {Object} options - Search options
 * @returns {Promise<Array>} Search results
 */
export async function searchVideos(query, options = {}) {
    try {
        const {
            hitsPerPage = 20,
            page = 0,
            filters = '',
            // attributesToRetrieve = ['*'],
            // attributesToHighlight = ['titleEN', 'titleTA', 'descriptionEN', 'descriptionTA']
        } = options;

        const searchParams = {
            query,
            hitsPerPage
        };

        if (filters) {
            searchParams.filters = filters;
        }

        console.log('searchParams', searchParams);

        const searchResponse = await searchClient.search([{
            indexName: ALGOLIA_INDEX_NAME,
            ...searchParams
        }]);
        
        const { hits, nbHits, nbPages } = searchResponse.results[0];
        console.log('searchResponse', searchResponse);
        console.log('hits', hits, 'nbHits', nbHits, 'nbPages', nbPages);
        return {
            results: hits,
            totalHits: nbHits,
            totalPages: nbPages,
            currentPage: page
        };
    } catch (error) {
        console.error('Algolia search error:', error);
        throw error;
    }
}

/**
 * Search videos with fallback to GraphQL if Algolia is not configured
 * @param {string} query - Search query
 * @param {Object} options - Search options
 * @returns {Promise<Array>} Search results
 */
export async function searchVideosWithFallback(query, options = {}) {
    // Check if Algolia is properly configured
    if (!ALGOLIA_APP_ID || !ALGOLIA_SEARCH_API_KEY || ALGOLIA_APP_ID === 'your-app-id' || ALGOLIA_SEARCH_API_KEY === 'your-search-api-key') {
        console.warn('Algolia not configured, falling back to GraphQL search');
        return null; // Return null to indicate fallback should be used
    }

    try {
        return await searchVideos(query, options);
    } catch (error) {
        console.error('Algolia search failed, falling back to GraphQL:', error);
        return null; // Return null to indicate fallback should be used
    }
}

/**
 * Search users (channels) using Algolia
 * @param {string} query - Search query
 * @param {Object} options - Search options
 * @returns {Promise<Object>} Search results
 */
export async function searchUsers(query, options = {}) {
    try {
        const {
            hitsPerPage = 10,
            page = 0,
            filters = ''
        } = options;

        const searchParams = {
            query,
            hitsPerPage
        };

        if (filters) {
            searchParams.filters = filters;
        }

        const searchResponse = await searchClient.search([{
            indexName: ALGOLIA_USERS_INDEX_NAME,
            ...searchParams
        }]);

        const { hits, nbHits, nbPages } = searchResponse.results[0];
        return {
            results: hits,
            totalHits: nbHits,
            totalPages: nbPages,
            currentPage: page
        };
    } catch (error) {
        console.error('Algolia user search error:', error);
        throw error;
    }
}

/**
 * Search users with a null fallback if Algolia is not configured or errors.
 * @param {string} query - Search query
 * @param {Object} options - Search options
 * @returns {Promise<Object|null>} Search results, or null to trigger fallback
 */
export async function searchUsersWithFallback(query, options = {}) {
    if (!ALGOLIA_APP_ID || !ALGOLIA_SEARCH_API_KEY || ALGOLIA_APP_ID === 'your-app-id' || ALGOLIA_SEARCH_API_KEY === 'your-search-api-key') {
        console.warn('Algolia not configured, skipping user search');
        return null;
    }

    try {
        return await searchUsers(query, options);
    } catch (error) {
        console.error('Algolia user search failed:', error);
        return null;
    }
}

/**
 * Add a video to Algolia index
 * @param {Object} video - Video object to index
 * @returns {Promise} Indexing result
 */
export async function addVideoToIndex() {
    try {
        throw createAlgoliaWriteNotConfiguredError();
    } catch (error) {
        console.warn('Skipping browser-side video indexing:', error);
        throw error;
    }
}

/**
 * Update a video in Algolia index
 * @param {Object} video - Updated video object
 * @returns {Promise} Update result
 */
export async function updateVideoInIndex() {
    return addVideoToIndex();
}

/**
 * Remove a video from Algolia index
 * @param {string} videoId - Video ID to remove
 * @returns {Promise} Deletion result
 */
export async function removeVideoFromIndex(videoId) {
    try {
        throw createAlgoliaWriteNotConfiguredError();
    } catch (error) {
        console.warn('Skipping browser-side video index deletion:', videoId, error);
        throw error;
    }
}

export default {
    searchVideos,
    searchVideosWithFallback,
    searchUsers,
    searchUsersWithFallback,
    addVideoToIndex,
    updateVideoInIndex,
    removeVideoFromIndex
};
